import React, { useEffect, useState } from 'react';
import { Col, Divider, Empty, Form, Row, Select, Space } from 'antd';
import { useParams } from 'react-router';
import { getHijriYear, getOperationType, rowStyle } from '../../components/common-constants';
import { hasWritePermission } from '../../../components/Authorization/hasWritePermission';
import { getFormItems } from '../../components/form/formComponents';
import { fetchData, saveData } from '../../service/dataServices';
import moment from 'moment';
import CustomAddNewPageContent from '../../../components/Content/CustomAddNewPageContent';

const { Option } = Select;

const SaveTechnicalForm = () => {
  const PATH = '/api/technical-forms';
  const { id } = useParams();
  const operation = getOperationType(id);
  const hasWritePerm = hasWritePermission('technicalForm');

  const [formObject] = Form.useForm();
  const [form] = Form.useForm();
  const [mainImageFileList, setMainImageFileList] = useState([]);
  const [imageList, setImageList] = useState([]);
  const [initialData, setInitialData] = useState();
  const [dropdowns, setDropdowns] = useState();
  const [initialOtherDimensions, setInitialOtherDimensions] = useState([]);
  const [objects, setObjects] = useState([]);
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [objectFilters, setObjectFilters] = useState({
    page: 0,
    size: 20,
  });

  const {
    getMultiselectFormItem,
    getInputFormItem,
    getInputNumberFormItem,
    getDatePickerFormItem,
    getMainImageFormItem,
    getTextAreaFormItem,
    getFormSubmitButton,
    getOtherDimensionsFormItems,
    getSwitchFormItem,
    getDropdownFormItem,
  } = getFormItems(dropdowns, hasWritePerm);

  fetchData('/api/dropdowns', setDropdowns);
  if (id) {
    fetchData(PATH + '/' + id, setInitialData);
  }
  const objectResponse = fetchData('/api/objects', data => setObjects(data.content), objectFilters);
  const saveMutation = saveData(PATH, operation.method);
  const fetchObjects = fetchData('/api/objects/' + selectedObjectId, setSelectedObject, null, false);

  useEffect(() => {
    if (selectedObjectId) {
      fetchObjects.refetch().then(resp => {
        let data = resp.data.data;
        const found = objects.find(o => o.id === data.id);
        if (!found) {
          setObjects(prev => [...prev, { id: data.id, inventoryNumber: data.inventoryNumber }]);
        }
      });
    }
  }, [selectedObjectId]);

  useEffect(() => {
    if (selectedObject) {
      setInitialOtherDimensions(selectedObject.otherDimensions || []);
      if (selectedObject.mainImage) {
        setMainImageFileList([selectedObject.mainImage]);
      } else {
        setMainImageFileList([]);
      }
      formObject.setFieldsValue(selectedObject);
    }
  }, [selectedObject]);

  useEffect(() => {
    if (initialData && dropdowns) {
      setSelectedObjectId(initialData.objectId);
      form.setFieldsValue({
        ...initialData,
        recordingDate: initialData.recordingDate ? moment(initialData.recordingDate) : null,
      });
    }
  }, [initialData, dropdowns]);

  const onSwitchUpdate = (values) => {
    form.setFieldsValue({ isTankFilled: values });
  };

  const onSearch = val => {
    if (val.length >= 4) {
      setObjectFilters((prev) => {
        return ({ ...prev, inventoryNumber: val });
      });
    }
  };

  const onClearSelectedObject = () => {
    setSelectedObjectId(null);
    setSelectedObject(null);
    formObject.resetFields();
    setInitialOtherDimensions([]);
    setMainImageFileList([]);
  };

  const onFinish = (values) => {
    const dataToSend = {
      ...values,
      objectId: selectedObjectId,
    };
    if (id) {
      saveMutation.mutate({ dataToSend, id });
    } else {
      saveMutation.mutate({ dataToSend });
    }
  };

  return (
    <CustomAddNewPageContent title={operation.type + ' Technical Form'} subTitle="Technical Forms">
      <Row style={{ marginLeft: 20, marginRight: 20 }}>
        <Select
          showSearch
          placeholder="Find object by inventory number"
          allowClear
          value={selectedObjectId}
          onClear={onClearSelectedObject}
          onSelect={value => setSelectedObjectId(value)}
          onSearch={onSearch}
          filterOption={false}
          loading={objectResponse.isFetching}
          style={{ width: '36vh' }}
        >
          {
            objects.map(option => (
              <Option value={option.id} key={option.id}>{option.inventoryNumber}</Option>
            ))
          }
        </Select>
      </Row>
      <Divider/>
      <Form
        form={formObject}
        labelCol={{ span: 16 }}
        wrapperCol={{ span: 16 }}
        layout="vertical"
        autoComplete="off"
      >
        <Row style={rowStyle}>
          <Col span={8} style={{ display: 'flex', alignItems: 'center' }}>
            {getMainImageFormItem('mainImage', true, 'objects', mainImageFileList, setMainImageFileList)}
            {mainImageFileList && mainImageFileList.length === 0 && (
              <div>
                <Space direction="vertical">
                  <Empty description={false}/>
                </Space>
              </div>
            )}
          </Col>
          <Col span={8}>
            {getInputFormItem('inventoryNumber', 'Inventory Number', false, 'text', true)}
            {getInputFormItem('objectName', 'Object Name', false, 'text', true)}
            {getInputFormItem('objectTitle', 'Object Title', false, 'text', true)}
            {getInputFormItem('author', 'Author', false, 'text', true)}
            {getMultiselectFormItem('material', 'Material', true)}
            {getMultiselectFormItem('technique', 'Technique', true)}
          </Col>
          <Col span={8}>
            {getInputNumberFormItem('length', 'Length', 'cm', true)}
            {getInputNumberFormItem('width', 'Width', 'cm', true)}
            {getInputNumberFormItem('height', 'Height', 'cm', true)}
            {getInputNumberFormItem('weight', 'Weight', 'gr', true)}
            {getOtherDimensionsFormItems(initialOtherDimensions, true)}
          </Col>
        </Row>
      </Form>
      <Divider/>

      <Form
        form={form}
        labelCol={{ span: 16 }}
        wrapperCol={{ span: 16 }}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
      >
        <Row style={rowStyle}>
          <Col span={6}>
            {getInputNumberFormItem('numOfCylinders', 'Number of Cylinders', 'V')}
            {getInputNumberFormItem('horsePower', 'Horse Power', 'Kmh')}
            {getInputNumberFormItem('maxSpeed', 'Max Speed', 'Km/h')}
            {getInputNumberFormItem('engineSize', 'Liter, Engine', 'L')}
            {getInputNumberFormItem('mileage', 'Mileage', 'Km')}
            {getSwitchFormItem('isTankFilled', 'Tank Filled', onSwitchUpdate)}
          </Col>
          <Col span={18}>
            {getTextAreaFormItem('mounting', 'Mounting', {span: 22}, false, 1600, 5)}
            {getDropdownFormItem('displayType', 'Display Type')}
            {getTextAreaFormItem('specs', 'Specs', {span: 22}, false, 1600, 5)}
          </Col>
        </Row>
        <Divider />
        <Row style={rowStyle}>
          <Col span={6}>
            {getTextAreaFormItem('sound', 'Sound', {span: 20}, false, 1600, 9)}
          </Col>
          <Col span={18}>
            {getTextAreaFormItem('lighting', 'Lighting', {span: 22}, false, 1600, 3)}
            {getTextAreaFormItem('powerSupply', 'Power Supply', {span: 22}, false, 1600, 3)}
          </Col>
        </Row>
        <Divider/>
        <Row style={rowStyle}>
          <Col span={12}>
            {getDatePickerFormItem('recordingDate', 'Recording Date')}
          </Col>
          <Col span={12}>
            {getInputFormItem('recordedBy', 'Recorded By')}
          </Col>
        </Row>
        <Divider/>
        {getFormSubmitButton(!selectedObject)}

      </Form>
    </CustomAddNewPageContent>
  );
};

export default SaveTechnicalForm;
